import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopSheetStyle } from "@/hooks/use-desktop-layout";
import { Booking } from "@/lib/services/bookings";
import referralService from "@/lib/services/referrals";
import { User } from "@/lib/types/user";
import { money } from "@/lib/utils";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import {
  ActivityIndicator,
  Modal,
  StyleSheet,
  Switch,
  TouchableOpacity,
  View,
} from "react-native";

interface PaymentSummarySheetProps {
  visible: boolean;
  booking: Booking;
  user: User | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (useWalletBalance: boolean) => void;
}

export function PaymentSummarySheet({
  visible,
  booking,
  user,
  submitting,
  onClose,
  onConfirm,
}: PaymentSummarySheetProps) {
  const { theme } = useAuth();
  const [useWallet, setUseWallet] = useState(false);
  const desktopSheetStyle = useDesktopSheetStyle();

  const isCustomerMode = user?.active_mode === "CUSTOMER";
  const { data: referralSummary, isLoading: walletLoading } = useQuery({
    queryKey: ["referrals"],
    queryFn: () => referralService.summary(),
    enabled: isCustomerMode && visible,
  });
  const canSpendWallet =
    isCustomerMode && (walletLoading || !!referralSummary?.can_apply_to_booking);

  const bookingTotal = Number(
    booking.pricing?.total_amount || booking.total_amount || 0,
  );
  const walletBalance = Number(
    referralSummary?.wallet_balance ?? user?.wallet_balance ?? 0,
  );
  const redemptionRate = referralSummary?.redemption_rate ?? 0;
  const walletCap = Math.round(bookingTotal * redemptionRate * 100) / 100;
  const walletDiscount =
    Math.round(Math.max(Math.min(walletBalance, walletCap), 0) * 100) / 100;
  const hasWalletCredit = walletDiscount >= 0.01;
  const applyWallet = useWallet && hasWalletCredit;
  const redemptionPercent = Math.round(redemptionRate * 100);
  const payableTotal =
    Math.round((bookingTotal - (applyWallet ? walletDiscount : 0)) * 100) /
    100;

  const rows: { label: string; value: string }[] = [
    { label: "Service", value: booking.service_type_label },
    { label: "Vehicle", value: booking.vehicle_registration },
    { label: "Amount", value: money(bookingTotal) },
  ];

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={submitting ? undefined : onClose}
    >
      <View style={styles.overlay}>
        <View
          style={[
            styles.sheet,
            { backgroundColor: theme.background },
            desktopSheetStyle,
          ]}
        >
          <TouchableOpacity
            onPress={onClose}
            disabled={submitting}
            accessibilityLabel="Close payment summary"
            style={styles.closeButton}
          >
            <Ionicons name="close" size={24} color={theme.icon} />
          </TouchableOpacity>

          <ThemedText style={styles.amount}>{money(payableTotal)}</ThemedText>

          <View style={[styles.card, { backgroundColor: theme.card }]}>
            {rows.map((row) => (
              <View key={row.label} style={styles.row}>
                <ThemedText style={styles.label}>{row.label}</ThemedText>
                <ThemedText style={styles.value} numberOfLines={1}>
                  {row.value}
                </ThemedText>
              </View>
            ))}

            {canSpendWallet && (
              <View style={styles.row}>
                <ThemedText style={styles.label}>Wallet balance</ThemedText>
                {walletLoading ? (
                  <ActivityIndicator size="small" color={BrandColors.primary} />
                ) : hasWalletCredit ? (
                  <View style={styles.pointsValue}>
                    <ThemedText style={styles.value}>
                      {`Use ${money(walletDiscount)} of ${money(walletBalance)}`}
                    </ThemedText>
                    <Switch
                      value={applyWallet}
                      onValueChange={setUseWallet}
                      disabled={submitting}
                      trackColor={{ true: BrandColors.primary }}
                      accessibilityLabel="Use wallet balance"
                    />
                  </View>
                ) : (
                  <ThemedText style={styles.value}>None available</ThemedText>
                )}
              </View>
            )}

            {applyWallet && (
              <View style={styles.row}>
                <ThemedText style={styles.label}>Wallet credit</ThemedText>
                <ThemedText style={[styles.value, styles.discount]}>
                  -{money(walletDiscount)}
                </ThemedText>
              </View>
            )}
          </View>

          {canSpendWallet && hasWalletCredit && (
            <ThemedText type="extraSmall" style={styles.note}>
              Wallet credit can cover up to {redemptionPercent}% of the service
              total. Pending rewards cannot be used, and the final amount is
              confirmed in the next step.
            </ThemedText>
          )}

          <TouchableOpacity
            style={[styles.payButton, submitting && styles.disabledButton]}
            onPress={() => onConfirm(applyWallet)}
            disabled={submitting}
          >
            {submitting ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <ThemedText style={styles.payButtonText} type="defaultSemiBold">
                Pay {money(payableTotal)}
              </ThemedText>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "flex-end",
  },
  sheet: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    paddingBottom: 36,
    gap: 16,
  },
  closeButton: {
    alignSelf: "flex-start",
  },
  amount: {
    fontSize: 32,
    lineHeight: 40,
    fontWeight: "700",
    textAlign: "center",
  },
  card: {
    borderRadius: 12,
    padding: 16,
    gap: 16,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    minHeight: 24,
  },
  label: {
    color: BrandColors.mediumGray,
    fontSize: 14,
  },
  value: {
    flexShrink: 1,
    fontSize: 14,
    textAlign: "right",
  },
  pointsValue: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  discount: {
    color: BrandColors.error,
  },
  note: {
    color: BrandColors.mediumGray,
  },
  payButton: {
    backgroundColor: BrandColors.success,
    borderRadius: 999,
    paddingVertical: 16,
    alignItems: "center",
  },
  payButtonText: {
    color: "#fff",
    fontSize: 16,
  },
  disabledButton: {
    opacity: 0.7,
  },
});
